import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowRight,
  ShieldCheck,
  Factory,
  MapPin,
  Wrench,
  Sparkles,
  Building2,
  Star,
  Cog,
  ChevronRight,
  Smartphone,
  Bell,
  Activity,
  Cpu,
  LogIn,
} from "lucide-react";
import SEO from "@/components/SEO";
import ContactForm from "@/components/ContactForm";
import FAQSection from "@/components/FAQSection";
import { BUSINESS, CITIES, MEDIA } from "@/constants/business";
import { HOME_FAQS } from "@/constants/faqs";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

export default function HomePage() {
  return (
    <>
      <SEO
        title="Vila Elevators & Escalators | Manufacturer in Bangalore | Karnataka, AP, Telangana, Tamil Nadu"
        description="Vila Elevators & Escalators — Bangalore manufacturer of smart elevators, home lifts, freight elevators, escalators & moving walkways. Every lift powered by an intelligent controller with web-app monitoring. Installation, maintenance, modernization across South India."
        path="/"
        keywords="elevators bangalore, escalator manufacturer bangalore, smart elevator, iot enabled elevator, elevator web-app monitoring, lift company karnataka, home lift bangalore, vila elevators"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: BUSINESS.name,
          url: "https://vilaelevators.in",
          telephone: BUSINESS.phoneRaw,
          email: BUSINESS.email,
        }}
      />

      {/* Hero */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={MEDIA.hero}
            alt="Modern elevator shaft interior"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#faf7f2] via-[#faf7f2]/80 to-[#faf7f2]/40" />
          <div className="absolute inset-0 bg-grid opacity-40" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full pt-28 pb-20">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 border border-[#b08d57]/40 bg-[#b08d57]/5 px-4 py-2 mb-8">
              <Sparkles className="w-3 h-3 text-[#b08d57]" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57]">
                South India's Premier Manufacturer
              </span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-[#1a1a1a] mb-6 leading-[0.95]">
              Elevators &<br />
              <span className="text-[#b08d57]">Escalators</span>,
              <br /> engineered to elevate.
            </h1>
            <p className="text-[#4a4a4a] text-lg sm:text-xl leading-relaxed mb-10 max-w-2xl">
              Manufactured in-house at our Bommasandra facility. Serving Karnataka, Andhra Pradesh, Telangana &amp; Tamil Nadu with smooth, safe, and dependable vertical transportation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/products"
                data-testid="hero-cta-products"
                className="bg-[#b08d57] hover:bg-[#8a6a3d] text-white px-8 py-4 font-semibold uppercase tracking-wider text-sm transition-colors flex items-center justify-center gap-2 group"
              >
                Explore Products
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-quote"
                className="border-2 border-[#e5ddd0] hover:border-[#b08d57] hover:text-[#b08d57] text-[#1a1a1a] px-8 py-4 font-semibold uppercase tracking-wider text-sm transition-colors flex items-center justify-center gap-2"
              >
                Request A Quote
              </Link>
            </div>

            <div className="mt-16 grid grid-cols-2 gap-6 max-w-md">
              <Stat number="15+" label="Years of Craft" />
              <Stat number="4" label="States Served" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Marquee */}
      <section className="border-y border-[#e5ddd0] py-10 bg-[#faf7f2] overflow-hidden">
        <Marquee gradient={false} speed={40} pauseOnHover>
          <div className="flex items-center gap-16 pr-16">
            {[
              "Installation",
              "Maintenance",
              "Modernization",
              "Repair",
              "Custom Design",
              "24/7 Service",
              "AMC",
              "CNC Precision",
            ].map((t, i) => (
              <React.Fragment key={i}>
                <span className="font-display font-black text-5xl sm:text-6xl text-stroke uppercase tracking-tight">
                  {t}
                </span>
                <span className="text-[#b08d57] text-4xl">✦</span>
              </React.Fragment>
            ))}
          </div>
        </Marquee>
      </section>

      {/* Services */}
      <section data-testid="services-section" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="max-w-2xl mb-16">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">Our Services</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1a1a1a]">
              End-to-end vertical transportation solutions.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ServiceCard
              icon={<Building2 className="w-8 h-8" />}
              title="Installation"
              desc="Expert setup ensuring safety, code compliance and smooth operation from day one — residential, commercial and industrial."
            />
            <ServiceCard
              icon={<Wrench className="w-8 h-8" />}
              title="Maintenance"
              desc="Preventive AMC programs, 24/7 emergency response and predictive checks that keep your lifts running flawlessly."
            />
            <ServiceCard
              icon={<Cog className="w-8 h-8" />}
              title="Modernization & Repair"
              desc="Upgrade legacy elevators with modern controls, energy-efficient motors and refreshed cabin interiors."
            />
          </div>
        </div>
      </section>

      {/* Smart Elevator — IoT-Enabled */}
      <section data-testid="smart-elevator-section" className="relative py-24 sm:py-32 overflow-hidden border-t border-[#e5ddd0]">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#b08d57]/60 via-[#faf7f2] to-[#faf7f2]" />
          <div className="absolute inset-0 bg-grid opacity-30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            variants={fadeUp}
            className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
          >
            <div>
              <div className="inline-flex items-center gap-2 border border-[#b08d57]/60 bg-[#b08d57]/10 px-4 py-2 mb-6">
                <Cpu className="w-3 h-3 text-[#b08d57]" />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57]">
                  Smart Elevator · IoT-Enabled
                </span>
              </div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#1a1a1a] leading-[1.05] mb-6">
                Monitor your elevator<br />
                <span className="text-[#b08d57]">from anywhere.</span>
              </h2>
              <p className="text-[#4a4a4a] text-lg leading-relaxed mb-8 max-w-xl">
                Every Vila elevator is powered by an <strong className="text-[#1a1a1a]">industry-leading intelligent controller</strong> — the same class of smart controllers used by leading global elevator brands. Pair it with the Vila web app and you'll get real-time visibility into your elevator's health, usage, and alerts, right from anywhere — no download needed.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                <SmartFeature icon={<Activity className="w-5 h-5" />} title="Real-time Health" desc="Live status, ride count, floor position." />
                <SmartFeature icon={<Bell className="w-5 h-5" />} title="Instant Alerts" desc="Breakdowns & maintenance push notifications." />
                <SmartFeature icon={<Wrench className="w-5 h-5" />} title="Remote Diagnostics" desc="Our service team sees issues before you call." />
                <SmartFeature icon={<Smartphone className="w-5 h-5" />} title="Ride History" desc="Analytics on usage patterns & peak hours." />
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/products/passenger-elevators"
                  data-testid="smart-cta-learn"
                  className="bg-[#b08d57] hover:bg-[#8a6a3d] text-white px-8 py-4 font-semibold uppercase tracking-wider text-sm transition-colors inline-flex items-center justify-center gap-2 group"
                >
                  See Smart Elevators <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/contact"
                  className="border-2 border-[#e5ddd0] hover:border-[#b08d57] hover:text-[#b08d57] text-[#1a1a1a] px-8 py-4 font-semibold uppercase tracking-wider text-sm transition-colors inline-flex items-center justify-center gap-2"
                >
                  Request a Demo
                </Link>
              </div>
            </div>

            {/* App mockup */}
            <div className="relative flex justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute -inset-8 bg-[#b08d57]/10 blur-3xl rounded-full" />
                <div className="relative w-72 sm:w-80 bg-[#faf7f2] border-2 border-[#e5ddd0] rounded-[42px] p-4 shadow-2xl">
                  {/* Phone notch */}
                  <div className="w-24 h-6 bg-[#faf7f2] rounded-full mx-auto mb-4" />
                  {/* App content */}
                  <div className="bg-[#faf7f2] rounded-[28px] p-5 space-y-4 border border-[#e5ddd0]">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-[10px] uppercase text-[#8a8a8a] tracking-widest">Building A</div>
                        <div className="font-display font-bold text-[#1a1a1a] text-lg">Passenger Lift #1</div>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs">
                        <div className="w-2 h-2 rounded-full bg-[#b08d57] animate-pulse" />
                        <span className="text-[#b08d57] font-semibold">Live</span>
                      </div>
                    </div>

                    <div className="border border-[#e5ddd0] p-4">
                      <div className="text-[10px] uppercase text-[#8a8a8a] tracking-widest mb-1">Current Floor</div>
                      <div className="font-display text-5xl font-black text-[#b08d57]">7</div>
                      <div className="text-xs text-[#6b6b6b] mt-1">↑ Ascending to 12</div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="border border-[#e5ddd0] p-3">
                        <div className="text-[10px] uppercase text-[#8a8a8a] tracking-widest">Rides Today</div>
                        <div className="font-display text-2xl font-bold text-[#1a1a1a]">142</div>
                      </div>
                      <div className="border border-[#e5ddd0] p-3">
                        <div className="text-[10px] uppercase text-[#8a8a8a] tracking-widest">Health</div>
                        <div className="font-display text-2xl font-bold text-[#b08d57]">98%</div>
                      </div>
                    </div>

                    <div className="border border-[#b08d57]/40 bg-[#b08d57]/5 p-3 flex items-start gap-2">
                      <Bell className="w-3.5 h-3.5 text-[#b08d57] mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-xs font-bold text-[#b08d57]">Service scheduled</div>
                        <div className="text-[10px] text-[#6b6b6b]">Preventive check · Tue 10 AM</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="absolute -bottom-6 -left-6 hidden sm:flex bg-[#faf7f2] border border-[#b08d57]/40 px-4 py-3 items-center gap-3">
                  <Cpu className="w-5 h-5 text-[#b08d57]" />
                  <div>
                    <div className="text-[10px] uppercase text-[#8a8a8a] tracking-widest">Powered by</div>
                    <div className="text-[#1a1a1a] font-display font-bold text-sm">Intelligent Controller</div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Customer Portal */}
      <section data-testid="customer-portal-section" className="py-16 border-t border-[#e5ddd0] bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col lg:flex-row lg:items-center gap-10">
          <div className="flex-1">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-3">Customer Portal</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">
              Already a Vila customer? Your elevator is one login away.
            </h2>
            <p className="text-white/70 text-base leading-relaxed max-w-2xl">
              Portal access is provided to Vila customers with smart-enabled elevators. Log in at <span className="text-[#b08d57] font-semibold">app.vilaelevators.in</span> to see everything about your installation.
            </p>
          </div>
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <PortalFeature icon={<Activity className="w-5 h-5" />} title="Live Monitoring" desc="Status, floor position & health score in real time." />
            <PortalFeature icon={<Bell className="w-5 h-5" />} title="Instant Alerts" desc="Breakdown & maintenance notifications." />
            <PortalFeature icon={<Wrench className="w-5 h-5" />} title="Service History" desc="Every visit, report & upcoming AMC schedule." />
          </div>
          <div className="lg:pl-4">
            <a
              href={BUSINESS.portalUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="portal-login-cta"
              className="bg-[#b08d57] hover:bg-[#8a6a3d] text-white px-8 py-4 font-semibold uppercase tracking-wider text-sm transition-colors inline-flex items-center gap-2 whitespace-nowrap"
            >
              <LogIn className="w-4 h-4" /> Customer Login
            </a>
          </div>
        </div>
      </section>

      {/* Manufacturing */}
      <section data-testid="manufacturing-section" className="py-24 sm:py-32 bg-[#faf7f2]/40 border-y border-[#e5ddd0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="relative">
            <img src={MEDIA.productElevator} alt="Vila passenger elevator cabin interior" className="w-full aspect-[4/5] object-cover" />
            <div className="absolute -bottom-6 -right-6 bg-[#b08d57] p-6 hidden md:block">
              <Factory className="w-10 h-10 text-[#1a1a1a] mb-2" />
              <div className="text-[#1a1a1a] text-xs font-bold uppercase tracking-widest">In-House CNC Facility</div>
            </div>
          </motion.div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">Manufacturing</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1a1a1a] mb-6">
              In-house precision. From raw metal to finished elevator.
            </h2>
            <p className="text-[#6b6b6b] text-lg leading-relaxed mb-8">
              Our production facility at Bommasandra Industrial Area is outfitted with state-of-the-art CNC machinery. Every cabin panel, door assembly, threshold and controller enclosure is manufactured in-house — giving us end-to-end control over quality, cost, and delivery timelines.
            </p>
            <div className="grid grid-cols-3 gap-6 mb-8">
              <Stat number="CNC" label="Precision" small />
              <Stat number="100%" label="In-House" small />
              <Stat number="ISO" label="Standards" small />
            </div>
            <Link
              to="/manufacturing"
              data-testid="manufacturing-cta"
              className="inline-flex items-center gap-2 text-[#b08d57] hover:text-[#b08d57] font-semibold uppercase text-sm tracking-wider group"
            >
              Tour Our Facility <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Products Bento */}
      <section data-testid="products-section" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="max-w-2xl mb-16">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">Product Range</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1a1a1a]">
              Built for every building — from villas to skylines.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <ProductCard
              className="md:col-span-8 md:row-span-2 aspect-[4/5] md:aspect-auto md:min-h-[600px]"
              image={MEDIA.productElevator}
              title="Passenger Elevators"
              tag="Flagship"
              to="/products/passenger-elevators"
              desc="Whisper-quiet MRL and geared systems for residential towers, commercial complexes and hotels."
            />
            <ProductCard
              className="md:col-span-4 aspect-video md:aspect-auto md:min-h-[290px]"
              image={MEDIA.homeLiftLifestyle}
              title="Home Lifts"
              tag="Residential"
              to="/products/home-lifts"
              desc="Compact, hydraulic and traction options for villas and low-rise homes."
            />
            <ProductCard
              className="md:col-span-4 aspect-video md:aspect-auto md:min-h-[290px]"
              image={MEDIA.freightEntrance}
              title="Freight Elevators"
              tag="Industrial"
              to="/products/freight-elevators"
              desc="Heavy-duty cargo lifts engineered for factories & warehouses."
            />
            <ProductCard
              className="md:col-span-12 aspect-[16/8] md:aspect-[16/6]"
              image={MEDIA.productEscalator}
              title="Escalators & Moving Walkways"
              tag="Commercial"
              to="/products/escalators"
              desc="High-flow escalators and horizontal walkways for malls, metros, airports and transit hubs."
            />
          </div>
        </div>
      </section>

      {/* Coverage */}
      <section data-testid="coverage-section" className="py-24 sm:py-32 bg-[#faf7f2]/40 border-y border-[#e5ddd0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="max-w-2xl mb-16">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">Service Area</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1a1a1a] mb-4">
              Elevators across four South Indian states.
            </h2>
            <p className="text-[#6b6b6b] text-lg">
              Headquartered in Bengaluru, our teams travel across Karnataka, Andhra Pradesh, Telangana and Tamil Nadu for site surveys, installation and service.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#f5efe6]">
            {CITIES.map((c) => (
              <Link
                key={c.slug}
                to={`/elevators-${c.slug}`}
                data-testid={`coverage-city-${c.slug}`}
                className="bg-[#faf7f2] hover:bg-[#faf7f2] p-8 group transition-colors"
              >
                <MapPin className="w-5 h-5 text-[#b08d57] mb-4" />
                <div className="font-display text-xl font-bold text-[#1a1a1a] mb-1">{c.name}</div>
                <div className="text-xs text-[#8a8a8a] uppercase tracking-widest mb-4">{c.state}</div>
                <div className="text-[#b08d57] text-xs font-semibold uppercase tracking-wider flex items-center gap-1 opacity-70 group-hover:opacity-100">
                  Explore <ChevronRight className="w-3 h-3" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="testimonials-section" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="max-w-2xl mb-16">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">Trusted By</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1a1a1a]">
              Builders, hoteliers & industry leaders across South India.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Testimonial
              quote="Vila installed 6 elevators across our residential tower in Whitefield. Installation was on-time and the ride quality is genuinely premium."
              author="R. Krishnan"
              role="Project Director, Bengaluru Builders"
            />
            <Testimonial
              quote="Their AMC team responds in hours, not days. That reliability is priceless when you run a hospital."
              author="Dr. Anita R."
              role="Facilities Head, Chennai Hospital"
            />
            <Testimonial
              quote="Upgraded two aging elevators to modern MRL systems. Energy bills dropped visibly and complaints stopped."
              author="Vasanth Reddy"
              role="Owners' Association, Hyderabad"
            />
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-[#faf7f2]/40 border-y border-[#e5ddd0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <WhyItem icon={<Factory />} title="In-House Manufacturing" desc="Full control over quality and lead time." />
            <WhyItem icon={<Cpu />} title="Smart App-Monitored" desc="Intelligent controller + Vila web app." />
            <WhyItem icon={<ShieldCheck />} title="Safety-First Engineering" desc="Every product exceeds IS 14665 safety norms." />
            <WhyItem icon={<Wrench />} title="24×7 Service Team" desc="Rapid response teams across 4 states." />
          </div>
        </div>
      </section>

      {/* FAQ — AEO */}
      <FAQSection
        faqs={HOME_FAQS}
        title="Elevator questions, answered directly."
        intro="Quick, factual answers about elevator costs, timelines, safety and service in South India — from the manufacturer itself."
      />

      {/* Contact */}
      <section data-testid="contact-section" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-4">Get In Touch</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#1a1a1a] mb-6">
              Elevate your project today.
            </h2>
            <p className="text-[#6b6b6b] text-lg leading-relaxed mb-10">
              Share your building specs — we'll respond with a tailored quote within one business day.
            </p>

            <div className="space-y-6 border-t border-[#e5ddd0] pt-8">
              <ContactRow label="Visit our factory" value={`${BUSINESS.address.line1}, ${BUSINESS.address.line2}, ${BUSINESS.address.city} - ${BUSINESS.address.postal}`} />
              <ContactRow label="Call us" value={BUSINESS.phone} link={`tel:${BUSINESS.phoneRaw}`} />
              <ContactRow label="Email us" value={BUSINESS.email} link={`mailto:${BUSINESS.email}`} />
              <ContactRow label="Working hours" value={BUSINESS.hours} />
            </div>

            <div className="mt-10 border border-[#e5ddd0] aspect-video overflow-hidden" data-testid="google-map-embed">
              <iframe
                title="Vila Elevators location - Bommasandra Industrial Area, Bengaluru"
                src={BUSINESS.mapEmbedNoKey}
                loading="lazy"
                className="w-full h-full grayscale-[30%]"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <div className="border border-[#e5ddd0] bg-[#faf7f2]/50 backdrop-blur-sm p-8 lg:p-10">
            <h3 className="font-display text-2xl font-bold text-[#1a1a1a] mb-6">Request a Quote</h3>
            <ContactForm sourcePage="home" />
          </div>
        </div>
      </section>
    </>
  );
}

function PortalFeature({ icon, title, desc }) {
  return (
    <div className="border border-white/15 p-4">
      <div className="w-9 h-9 border border-[#b08d57]/50 text-[#b08d57] flex items-center justify-center mb-3">
        {icon}
      </div>
      <div className="font-display font-bold text-white text-sm mb-1">{title}</div>
      <div className="text-white/60 text-xs leading-relaxed">{desc}</div>
    </div>
  );
}

function SmartFeature({ icon, title, desc }) {
  return (
    <div className="border border-[#e5ddd0] bg-[#faf7f2]/60 p-4 backdrop-blur-sm">
      <div className="w-9 h-9 border border-[#b08d57]/40 text-[#b08d57] flex items-center justify-center mb-3">
        {icon}
      </div>
      <div className="font-display font-bold text-[#1a1a1a] text-sm mb-1">{title}</div>
      <div className="text-[#6b6b6b] text-xs leading-relaxed">{desc}</div>
    </div>
  );
}


function Stat({ number, label, small }) {
  return (
    <div>
      <div className={`font-display font-black text-[#1a1a1a] ${small ? "text-2xl" : "text-3xl sm:text-4xl"}`}>{number}</div>
      <div className="text-xs text-[#8a8a8a] uppercase tracking-widest mt-1">{label}</div>
    </div>
  );
}

function ServiceCard({ icon, title, desc }) {
  return (
    <div className="border border-[#e5ddd0] bg-[#faf7f2]/50 backdrop-blur-sm p-8 hover:-translate-y-1 hover:border-[#b08d57]/50 transition-all duration-300">
      <div className="w-14 h-14 border border-[#b08d57]/30 flex items-center justify-center text-[#b08d57] mb-6">
        {icon}
      </div>
      <h3 className="font-display text-2xl font-bold text-[#1a1a1a] mb-3">{title}</h3>
      <p className="text-[#6b6b6b] leading-relaxed">{desc}</p>
    </div>
  );
}

function ProductCard({ image, title, desc, tag, to = "/products", className = "" }) {
  return (
    <Link
      to={to}
      className={`relative group overflow-hidden border border-[#e5ddd0] hover:border-[#b08d57]/50 transition-colors ${className}`}
    >
      <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#faf7f2] via-[#faf7f2]/40 to-transparent" />
      <div className="relative h-full flex flex-col justify-end p-6 md:p-8">
        <span className="self-start bg-[#b08d57] text-white text-[10px] uppercase font-bold tracking-widest px-3 py-1 mb-4">{tag}</span>
        <h3 className="font-display text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-2">{title}</h3>
        <p className="text-[#4a4a4a] text-sm max-w-md">{desc}</p>
      </div>
    </Link>
  );
}

function Testimonial({ quote, author, role }) {
  return (
    <div className="border border-[#e5ddd0] bg-[#faf7f2]/50 p-8">
      <div className="flex gap-1 mb-4">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="w-4 h-4 fill-[#b08d57] text-[#b08d57]" />
        ))}
      </div>
      <p className="text-[#4a4a4a] leading-relaxed mb-6 text-base">"{quote}"</p>
      <div>
        <div className="font-display font-bold text-[#1a1a1a]">{author}</div>
        <div className="text-xs text-[#8a8a8a] uppercase tracking-widest mt-1">{role}</div>
      </div>
    </div>
  );
}

function WhyItem({ icon, title, desc }) {
  return (
    <div>
      <div className="w-12 h-12 border border-[#b08d57]/40 text-[#b08d57] flex items-center justify-center mb-4">
        {icon}
      </div>
      <h4 className="font-display text-lg font-bold text-[#1a1a1a] mb-2">{title}</h4>
      <p className="text-[#6b6b6b] text-sm">{desc}</p>
    </div>
  );
}

function ContactRow({ label, value, link }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#b08d57] mb-1">{label}</div>
      {link ? (
        <a href={link} className="text-[#1a1a1a] hover:text-[#b08d57] transition-colors text-lg">{value}</a>
      ) : (
        <div className="text-[#1a1a1a] text-lg leading-relaxed">{value}</div>
      )}
    </div>
  );
}
